/**
 * Comando `screenshot` (API.md): disegna su un canvas quello che c'è a schermo (video/immagine in onda,
 * `object-fit: contain` su sfondo nero), lato lungo <= 1280, JPEG 0.8, upload multipart con `command_id`.
 * Se non c'è niente in onda si invia un JPEG nero. Gli errori vanno in `status.errors`, senza upload.
 */

export const MAX_SIDE = 1280;
export const JPEG_QUALITY = 0.8;

export interface Size {
  width: number;
  height: number;
}

/** Riduce (mai ingrandisce) mantenendo le proporzioni, lato lungo <= max. Minimo 1 px. */
export function fitSize(w: number, h: number, max = MAX_SIDE): Size {
  if (!(w > 0) || !(h > 0)) return { width: 16, height: 9 };
  const k = Math.min(1, max / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)) };
}

/** Rettangolo di destinazione per `object-fit: contain` (centrato). */
export function containRect(srcW: number, srcH: number, dstW: number, dstH: number): { x: number; y: number; w: number; h: number } {
  const k = Math.min(dstW / srcW, dstH / srcH);
  const w = srcW * k;
  const h = srcH * k;
  return { x: (dstW - w) / 2, y: (dstH - h) / 2, w, h };
}

/** Ciò che serve per catturare lo slot visibile. */
export interface Snapshot {
  element: CanvasImageSource;
  srcW: number;
  srcH: number;
  /** Dimensioni dello schermo (stage) in px CSS: il canvas ne ha le proporzioni. */
  stageW: number;
  stageH: number;
}

export interface Ctx2DLike {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect(x: number, y: number, w: number, h: number): void;
  drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
}
export interface CanvasLike {
  width: number;
  height: number;
  getContext(type: '2d'): Ctx2DLike | null;
  toBlob(cb: (b: Blob | null) => void, type: string, quality: number): void;
}

export async function captureJpeg(
  snap: Snapshot | null,
  fallbackStage: Size,
  makeCanvas: (w: number, h: number) => CanvasLike,
): Promise<Blob> {
  const stage = snap ? { width: snap.stageW, height: snap.stageH } : fallbackStage;
  const { width, height } = fitSize(stage.width, stage.height);
  const canvas = makeCanvas(width, height);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d non disponibile');
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, width, height);
  if (snap && snap.srcW > 0 && snap.srcH > 0) {
    const r = containRect(snap.srcW, snap.srcH, width, height);
    ctx.drawImage(snap.element, r.x, r.y, r.w, r.h);
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob ha restituito null'))), 'image/jpeg', JPEG_QUALITY);
  });
}

export interface ScreenshotCommandDeps {
  capture: () => Promise<Blob>;
  upload: (blob: Blob, commandId: number) => Promise<void>;
  onError: (msg: string) => void;
}

/** Esegue il comando: cattura, poi carica. Non lancia mai; ogni errore finisce in `onError`. */
export async function runScreenshotCommand(deps: ScreenshotCommandDeps, commandId: number): Promise<boolean> {
  let blob: Blob;
  try {
    blob = await deps.capture();
  } catch (e) {
    deps.onError(`screenshot: cattura fallita: ${e instanceof Error ? e.message : String(e)}`);
    return false;
  }
  try {
    await deps.upload(blob, commandId);
    return true;
  } catch (e) {
    deps.onError(`screenshot: upload fallito: ${e instanceof Error ? e.message : String(e)}`);
    return false;
  }
}
